/* eslint-disable @nx/enforce-module-boundaries */
import { canUseDOM, chaseCodes, amexCodes } from '@marriott/mi-ui-library';
import { isAuthenticated as isSharedAuthenticated } from '@marriott/mi-ui-library';
import { ColumnLayout, CardType, SELECTOR_LIST, SESSION_CONSTANTS } from '../../constants/constants';
import {
  CardData,
  CardEntry,
  ComparisonObject,
} from '../../../molecules/CardComparisonAccordion/CardComparisonAccordion.types';
import { targetPayloadRequest, useSession } from '@marriott/mi-headless-utils';
import { RefObject, useRef } from 'react';
import { FeatureIcon, FeatureIconColor, iconColorMap } from './enums';
import { useEffect, useState } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import { useRouter } from 'next/router';
import {
  DataLayerScript,
  AlloyResponse,
  Proposition,
  PropositionItem,
  TargetHookReturn,
  MutationObserverCleanup,
} from './helper.types';

declare global {
  interface Window {
    adobeDataLayer: {
      [key: number]: {
        event: string;
        [key: string]: Record<string, unknown> | string | undefined | boolean;
      };
      version: number;
      push: (event: { event: string; [key: string]: Record<string, unknown> | string | undefined | boolean }) => void;
      getState: () => Record<string, unknown>;
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
    };
  }
}

export const getColumnClass = (columnLayout: ColumnLayout, tabType: CardType): string => {
  if (columnLayout === '1-column') {
    return 'col-12 ';
  }

  if (columnLayout === '3-column') {
    return tabType === 'personal' ? 'col-6 col-md-4' : 'col-12 col-md-4';
  }

  return 'col-6';
};

export const getDataLayerScript = (dataLayer?: DataLayerScript): string => {
  const dataLayerValue = dataLayer?.data?.[0] ? JSON.stringify(dataLayer.data[0]) : '{}';
  const mvpOffersValue =
    dataLayer?.mvpOffersData && dataLayer.mvpOffersData !== 'null' ? dataLayer.mvpOffersData : '{}';
  return `var dataLayer = ${dataLayerValue}; var mvpOffers = ${mvpOffersValue};`;
};

export const removeExtraHeights = (ref: RefObject<HTMLDivElement>): void => {
  if (!canUseDOM || !ref.current) return;

  SELECTOR_LIST.forEach(({ selector, rowCount }) => {
    for (let row = 1; row <= rowCount; row++) {
      const elements = ref.current?.querySelectorAll<HTMLElement>(`.${selector}-${row}`);
      elements?.forEach(element => {
        if (element.style) {
          element.style.minHeight = '';
        }
      });
    }
  });
};

export const equalizeBankLandingCards = (ref: RefObject<HTMLDivElement>): void => {
  if (!canUseDOM || !ref.current) return;

  requestAnimationFrame(() => {
    SELECTOR_LIST.forEach(({ selector, rowCount }) => {
      for (let row = 1; row <= rowCount; row++) {
        // Special handling for 'detail' selector: equalize inner components separately
        // This ensures precise height control for PointsCard and points-earned-property divs
        if (selector === 'detail') {
          // Equalize PointsCard components (main points display)
          const pointsCardNodes = ref.current?.querySelectorAll<HTMLElement>(
            `.${selector}-${row} [data-component-name="m-cobrand-PointsCard"]`
          );
          if (pointsCardNodes?.length) {
            let maxHeight = 0;
            pointsCardNodes.forEach(node => {
              maxHeight = Math.max(maxHeight, node.clientHeight);
            });
            pointsCardNodes.forEach(node => {
              if (node.style) {
                node.style.minHeight = `${maxHeight}px`;
              }
            });
          }

          // Equalize points-earned-property divs (detailed points list)
          const propertyNodes = ref.current?.querySelectorAll<HTMLElement>(
            `.${selector}-${row} .points-earned-property`
          );
          if (propertyNodes?.length) {
            let maxHeight = 0;
            propertyNodes.forEach(node => {
              maxHeight = Math.max(maxHeight, node.clientHeight);
            });
            // Add 16px to preserve border spacing
            const targetHeight = maxHeight + 16;
            propertyNodes.forEach(node => {
              if (node.style) {
                node.style.minHeight = `${targetHeight}px`;
              }
            });
          }
        } else {
          // Standard equalization for other selectors
          const nodes = ref.current?.querySelectorAll<HTMLElement>(`.${selector}-${row}`);
          if (!nodes?.length) continue;

          let maxHeight = 0;
          nodes.forEach(node => {
            maxHeight = Math.max(maxHeight, node.clientHeight);
          });

          nodes.forEach(node => {
            if (node.style) {
              node.style.minHeight = `${maxHeight}px`;
            }
          });
        }
      }
    });
  });
};

export const referAFriend = (dataLayer: Record<string, unknown>, ref: RefObject<HTMLDivElement>): void => {
  if (!ref.current || !isSharedAuthenticated()) return;

  const cardDetails = dataLayer['mr_prof_cc_all'];
  const detailsArray = cardDetails ? String(cardDetails).split(',') : [];
  const cardDetailsElements = ref.current.querySelectorAll<HTMLElement>('.refer-friend-link');

  if (!cardDetailsElements.length) return;

  const ccFromDom = Array.from(cardDetailsElements)
    .map(ele => ele.dataset['creditcardcode']?.trim())
    .filter((code): code is string => Boolean(code))
    .filter(code => detailsArray.includes(code));

  ccFromDom.forEach(code => {
    cardDetailsElements.forEach(ele => {
      if (ele.dataset['creditcardcode']?.trim() === code) {
        ele.classList.remove('d-none');
      }
    });
  });
};

export const getPricingContentForUser = (
  pricingTermsGuest?: string,
  pricingTermsSignedIn?: string
): string | undefined => {
  const userIsAuthenticated = canUseDOM ? isSharedAuthenticated() : false;
  if (userIsAuthenticated) {
    return pricingTermsSignedIn || pricingTermsGuest;
  }

  return pricingTermsGuest || pricingTermsSignedIn;
};

export const getUserLocation = (adobeDataLayer?: Record<string, unknown>): string | undefined => {
  const dataLayer =
    adobeDataLayer || (typeof window !== 'undefined' ? (window.dataLayer as Record<string, unknown>) : undefined);
  if (!dataLayer) return undefined;

  if (isSharedAuthenticated()) {
    return dataLayer['mr_prof_address_country'] as string | undefined;
  }

  return dataLayer['browser_akamai_loc_country'] as string | undefined;
};
export const setObserver = (componentId: string, ref: RefObject<HTMLDivElement>): MutationObserverCleanup => {
  const pageWcmMode = document.getElementsByTagName('html')[0];
  const updateStyleComponent = ref.current?.querySelector(`#${componentId}__slides`);

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.attributeName === 'class') {
        const currWcmMode = pageWcmMode.getAttribute('class');
        if (currWcmMode?.includes('Edit')) {
          updateStyleComponent?.classList.add('glide__slides_authoring');
        } else if (currWcmMode?.includes('Preview')) {
          updateStyleComponent?.classList.remove('glide__slides_authoring');
        }
      }
    });
  });

  observer.observe(pageWcmMode, { attributes: true });

  return () => observer.disconnect();
};

export const shouldCardDisplay = (cardType: string, activeTab: string): boolean => {
  if (!cardType || !activeTab) return true;

  if ((cardType === 'business' || cardType === 'personal') && cardType !== activeTab) {
    return false;
  }

  return true;
};

export const getIconColor = (status: FeatureIcon): FeatureIconColor => iconColorMap[status];

export const isMobile = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const isTablet = /Linux/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
  const isIpad = /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints < 6 && navigator.maxTouchPoints > 0;

  return (
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isTablet || isIpad
  );
};

export const useLandscapeOrientation = (): boolean => {
  const getInitialLandscape = () => {
    if (!canUseDOM) return false;
    return window.matchMedia('(orientation: landscape)').matches;
  };

  const [isLandscape, setIsLandscape] = useState(getInitialLandscape);

  useEffect(() => {
    if (!canUseDOM) return;

    const landscapeQuery = window.matchMedia('(orientation: landscape)');
    setIsLandscape(landscapeQuery.matches);

    const handleOrientationChange = (event: MediaQueryListEvent) => {
      setIsLandscape(event.matches);
    };

    landscapeQuery.addEventListener('change', handleOrientationChange);
    return () => landscapeQuery.removeEventListener('change', handleOrientationChange);
  }, []);

  return isLandscape;
};

export const extractAnnualFee = (innerHtml: string): string | undefined => {
  if (!innerHtml) return undefined;

  const lower = innerHtml.toLowerCase();
  let searchFrom = 0;
  let pOpen = lower.indexOf('<p', searchFrom);

  while (pOpen !== -1) {
    const gt = lower.indexOf('>', pOpen + 2);
    if (gt === -1) return undefined;

    const pClose = lower.indexOf('</p>', gt + 1);
    if (pClose === -1) return undefined;

    const content = innerHtml.slice(gt + 1, pClose);
    if (content.toLowerCase().includes('annual fee')) {
      return innerHtml.slice(pOpen, pClose + 4);
    }

    searchFrom = pClose + 4;
    pOpen = lower.indexOf('<p', searchFrom);
  }

  return undefined;
};

const isCardDataEntry = (entry: unknown): entry is CardData => {
  return typeof entry === 'object' && entry !== null && !Array.isArray(entry);
};

export const setCardArray = (cardArray?: ComparisonObject): CardData[] => {
  if (!cardArray) return [];

  const dataArray: CardData[] = [];

  Object.entries(cardArray).forEach(([cardKey, cardValue]) => {
    if (cardKey === 'categories' || !cardValue) return;

    const entries = Object.values(cardValue).filter(isCardDataEntry);
    const defaultCardType: CardType = (cardValue as CardEntry).cardType ?? 'none';

    entries.forEach(data => {
      dataArray.push({
        ...data,
        cardId: data.cardId ?? cardKey,
        cardType: (data.cardType as CardType | undefined) ?? defaultCardType,
      });
    });
  });

  return dataArray;
};

export const replaceWhitespaceAndAddBtn = (componentId?: string): string => {
  if (!componentId) return '';
  return componentId.replace(/\s+/g, '-') + '-btn';
};

export const useAlloyTargetMethod = ({
  targetMboxValue,
  includeDSTParameters = true,
}: {
  targetMboxValue?: string;
  includeDSTParameters?: boolean;
}): TargetHookReturn => {
  const router = useRouter();
  const queryParams = router.query;
  const DST_VALUES = [...amexCodes, ...chaseCodes];
  const isDSTvalue = DST_VALUES.some(key =>
    Object.keys(queryParams).some(param => param.toLowerCase() === key?.toLowerCase())
  );

  const [isTargetContentLoaded, setIsTargetContentLoaded] = useState(false);
  const [targetData, setTargetData] = useState<Record<string, unknown> | string>('');
  const { datalayer, mvpOffers, updateDatalayer } = useSession();
  let counter = 0;
  const hasCalledFetchTargetData = useRef(false);

  useEffect(() => {
    if (!isEmpty(datalayer) && !hasCalledFetchTargetData.current) {
      hasCalledFetchTargetData.current = true;

      if (targetMboxValue && (includeDSTParameters || !isDSTvalue)) {
        launchLoadedAddEventListener();
      } else {
        setIsTargetContentLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datalayer, targetMboxValue, isDSTvalue]);

  if (!targetMboxValue) {
    return { isTargetContentLoaded: true, targetData: '' };
  }

  const launchLoadedAddEventListener = () => {
    const handleLaunchLoaded = () => {
      if (counter < 1) {
        fetchTargetData(targetMboxValue);
        counter = counter + 1;
      }
    };

    if (window !== undefined && window.adobeDataLayer) {
      window?.adobeDataLayer?.addEventListener('launchLoaded', handleLaunchLoaded);
    }

    const timeoutId = setTimeout(() => {
      if (window !== undefined) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
        setIsTargetContentLoaded(true);
      }
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      if (window !== undefined && window.adobeDataLayer) {
        window?.adobeDataLayer?.removeEventListener('launchLoaded', handleLaunchLoaded);
      }
    };
  };

  const fetchTargetData = async (targetMboxValue?: string) => {
    const targetMbox = targetMboxValue ? [targetMboxValue] : undefined;
    const payLoad = targetPayloadRequest(
      {
        isUxl: 'false',
        locale:
          window.sessionData?.locale?.replace('-', '_') ||
          window._satellite?.['getVar']?.('brwsrLang') ||
          datalayer?.['brwsrLang'] ||
          'en_US',
        mboxName: targetMbox,
        propertyToken: useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'] as string,
      },
      datalayer,
      mvpOffers
    );
    try {
      const result = await window.alloy<AlloyResponse>('sendEvent', {
        data: {
          __adobe: {
            target: payLoad?.execute?.mboxes?.[0]?.parameters,
          },
        },
        xdm: {},
        decisionScopes: targetMbox,
      });

      const propositions = result?.propositions;
      let item: Proposition | undefined;
      let experience: string | undefined;
      let foundProposition = false;

      if (propositions && targetMboxValue) {
        for (const prop of propositions) {
          const proposition = prop as Proposition;
          const propItems = proposition.items;
          const firstItem = propItems?.[0] as PropositionItem;
          const itemScope = proposition.scope;
          item = proposition;

          if (itemScope === targetMboxValue && firstItem) {
            const responsivegridItems =
              firstItem.data?.content?.[':items']?.root?.[':items']?.responsivegrid?.[':items'];

            console.log('PROPOSITION SELECTED', responsivegridItems);
            experience = firstItem.meta?.['experience.name'];
            console.log('Experience name', experience);
            if (responsivegridItems) {
              setTargetData(responsivegridItems);
            }
            foundProposition = true;
          }
        }

        setIsTargetContentLoaded(true);

        if (foundProposition) {
          updateDatalayer({
            event: 'card-layout-updated',
            data: { item, experience },
            skipDatalayerUpdate: true,
          });
        }
      }

      if (item) {
        window.alloy<unknown>('sendEvent', {
          xdm: {
            eventType: 'decisioning.propositionDisplay',
            _experience: {
              decisioning: {
                propositions: [
                  {
                    id: item.id,
                    scope: item.scope,
                    scopeDetails: item.scopeDetails,
                  },
                ],
                propositionEventType: {
                  display: 1,
                },
              },
            },
          },
        });
      }
    } catch (error) {
      console.log('Error occurred in Target call', error);
      setIsTargetContentLoaded(true);
    }
  };

  return { isTargetContentLoaded, targetData };
};

export const normalizeAEMBoolean = (value?: boolean | string): boolean => {
  if (value === '') return false;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (typeof value === 'boolean') return value;
  return false;
};

export const getStoredCreditCardEndpoint = (fallback?: string): string | undefined => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const stored = sessionStorage?.getItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
  if (!stored) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(stored);
    return typeof parsed === 'string' ? parsed : fallback;
  } catch {
    return stored;
  }
};

export const persistComparisonCardSelection = (
  cardIds: string[],
  existingCobrandData?: Record<string, unknown>
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const selectedCards = cardIds.map(cardId => ({ cardname: cardId, checked: true }));
  const next = { ...(existingCobrandData ?? {}), selectedCards };
  sessionStorage?.setItem(SESSION_CONSTANTS.COBRAND_DATA, JSON.stringify(next));
};
